// src/pages/me/single-order.js
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import Client from "../../utils/client";
import { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { api_url } from "../../constant";
import OrderInfo from "./components/orders/OrderInfo";
import axios from "axios";
import { useTranslation } from "react-i18next";

// Format date function
const formatDate = (dateString) => {
    if (!dateString) return "غير محدد";
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleDateString('ar-EG', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    } catch (error) {
        return dateString;
    }
};

// Invoice Details Component
const InvoiceDetailsTab = ({ invoiceId }) => {
    const { t } = useTranslation();
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showConfirm, setShowConfirm] = useState(null);
    const [rejectionReason, setRejectionReason] = useState("");
    const token = localStorage.getItem("token");
    const currentUserId = localStorage.getItem("user_id");

    useEffect(() => {
        const fetchInvoice = async () => {
            if (!invoiceId) {
                setLoading(false);
                setError(t("order.invoice.noInvoice"));
                return;
            }

            try {
                const response = await fetch(`${api_url}/invoices/${invoiceId}`, {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("token"),
                    },
                });
                
                if (!response.ok) {
                    throw new Error(`Failed to fetch invoice: ${response.status}`);
                }
                
                const data = await response.json();
                
                const formattedInvoice = {
                    invoiceId: data.invoiceId,
                    issuedDate: data.issuedDate,
                    seller: data.seller,
                    buyer: data.buyer,
                    cropName: data.cropName,
                    quantity: data.quantity ?? t("common.notAvailable"),
                    amount: data.amount,
                    deliveryDate: data.deliveryDate,
                    notes: data.notes,
                    status: data.status,
                    unit: data.unit,
                    deliveryPlace: data.deliveryPlace,
                    deliveryResponsibility: data.deliveryResponsibility,
                    paymentTerms: data.paymentTerms,
                    negotiation: data.negotiation,
                };
                
                setInvoice(formattedInvoice);
            } catch (err) {
                console.error("Error fetching invoice:", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchInvoice();
    }, [invoiceId, t]);

    const handleAcceptInvoice = async () => {
        try {
            await axios.patch(`${api_url}/invoices/accept/${invoice?.invoiceId}`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setInvoice(prev => ({ ...prev, status: "accepted" }));
            toast.success(t("order.invoice.acceptSuccess"));
        } catch (error) {
            console.error("Error accepting invoice:", error.response?.data || error.message);
            toast.error(t("order.invoice.acceptFailed"));
        } finally {
            setShowConfirm(null);
        }
    };

    const handleRejectInvoice = async () => {
        if (!rejectionReason.trim()) {
            toast.error(t("order.invoice.rejectReasonRequired"));
            return;
        }
        try {
            await axios.patch(`${api_url}/invoices/reject/${invoice?.invoiceId}`, { rejectionReason }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setInvoice(prev => ({ ...prev, status: "rejected" }));
            setRejectionReason("");
            toast.success(t("order.invoice.rejectSuccess"));
        } catch (error) {
            console.error("Error rejecting invoice:", error.response?.data || error.message);
            toast.error(t("order.invoice.rejectFailed"));
        } finally {
            setShowConfirm(null);
        }
    };

    if (loading) {
        return <div className="text-center py-10 text-gray-500">{t("order.invoice.loading")}</div>;
    }

    if (error) {
        return <div className="text-center py-10 text-red-500">{t("order.invoice.error")}: {error}</div>;
    }

    if (!invoice) {
        return <div className="text-center py-10 text-gray-500">{t("order.invoice.noInvoice")}</div>;
    }

    const isCurrentUserSeller = currentUserId === invoice.negotiation?.sellerId;

    return (
        <div className="bg-white rounded-xl overflow-hidden shadow-md">
            {/* Invoice Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-5 text-white">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                    <div>
                        <h2 className="text-xl font-bold">{t("order.invoice.title")}</h2>
                        <p className="text-blue-100 mt-1">{t("order.invoice.number")}: #{invoice?.invoiceId}</p>
                    </div>
                    <div className="mt-3 md:mt-0">
                        <p className="text-blue-100 text-sm">{t("order.invoice.issueDate")}</p>
                        <p className="font-medium">{formatDate(invoice?.issuedDate)}</p>
                    </div>
                </div>
            </div>

            {/* Parties Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 p-5 border-b">
                <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">{t("order.invoice.seller")}</p>
                    <p className="font-semibold">{invoice?.seller}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">{t("order.invoice.buyer")}</p>
                    <p className="font-semibold">{invoice?.buyer}</p>
                </div>
            </div>

            {/* Items Table */}
            <div className="p-5">
                <h3 className="text-md font-bold mb-3">{t("order.invoice.details")}</h3>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gray-100">
                                <th className="text-right p-3 text-sm">{t("order.invoice.product")}</th>
                                <th className="text-center p-3 text-sm">{t("order.invoice.quantity")}</th>
                                <th className="text-center p-3 text-sm">{t("order.invoice.unit")}</th>
                                <th className="text-center p-3 text-sm">{t("order.invoice.price")}</th>
                                <th className="text-center p-3 text-sm">{t("order.invoice.total")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="border-b">
                                <td className="p-3">{invoice?.cropName}</td>
                                <td className="text-center p-3">{invoice?.quantity}</td>
                                <td className="text-center p-3">{invoice?.unit?.name || '-'}</td>
                                <td className="text-center p-3">{invoice?.amount} {t("common.currency")}</td>
                                <td className="text-center p-3 font-semibold">{invoice?.amount} {t("common.currency")}</td>
                            </tr>
                            <tr className="bg-gray-50">
                                <td colSpan="4" className="p-3 font-semibold text-left">{t("order.invoice.totalAmount")}:</td>
                                <td className="p-3 font-bold text-primary text-center">{invoice?.amount} {t("common.currency")}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Delivery Details */}
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5 border-t">
                <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-500">{t("order.invoice.deliveryPlace")}</p>
                    <p className="font-semibold">{invoice?.deliveryPlace || t("common.notSpecified")}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-500">{t("order.invoice.deliveryResponsibility")}</p>
                    <p className="font-semibold">{invoice?.deliveryResponsibility === "seller" ? t("order.invoice.seller") : t("order.invoice.buyer")}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-500">{t("order.invoice.deliveryDate")}</p>
                    <p className="font-semibold">{formatDate(invoice?.deliveryDate)}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-500">{t("order.invoice.paymentTerms")}</p>
                    <p className="font-semibold">{invoice?.paymentTerms || t("order.invoice.noPaymentTerms")}</p>
                </div>
            </div>

            {/* Notes */}
            {invoice?.notes && (
                <div className="px-5 pb-5">
                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                        <p className="text-sm text-blue-600 mb-1">{t("order.invoice.notes")}</p>
                        <p className="text-blue-800">{invoice?.notes}</p>
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            {invoice?.status === "pending" && !isCurrentUserSeller && (
                <div className="p-5 border-t bg-gray-50">
                    <div className="flex flex-col sm:flex-row justify-center gap-3">
                        <button
                            className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-5 rounded-lg transition flex items-center justify-center gap-2"
                            onClick={() => setShowConfirm("accept")}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            {t("order.invoice.accept")}
                        </button>
                        <button
                            className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-5 rounded-lg transition flex items-center justify-center gap-2"
                            onClick={() => setShowConfirm("reject")}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            {t("order.invoice.reject")}
                        </button>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            {showConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-5 rounded-xl shadow-xl max-w-sm w-full mx-4">
                        <h3 className="text-lg font-bold mb-3 text-center">
                            {showConfirm === 'accept' ? t("order.invoice.confirmAccept") : t("order.invoice.confirmReject")}
                        </h3>
                        <p className="text-gray-600 mb-4 text-center">
                            {showConfirm === 'accept' 
                                ? t("order.invoice.acceptMessage")
                                : t("order.invoice.rejectMessage")}
                        </p>
                        {showConfirm === 'reject' && (
                            <textarea
                                placeholder={t("order.invoice.rejectReasonPlaceholder")}
                                className="w-full p-2 border rounded-lg mb-3 text-right"
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                rows="3"
                            />
                        )}
                        <div className="flex gap-3">
                            <button
                                onClick={showConfirm === 'accept' ? handleAcceptInvoice : handleRejectInvoice}
                                className={`flex-1 py-2 rounded-lg font-semibold text-white ${showConfirm === 'accept' ? 'bg-green-500' : 'bg-red-500'}`}
                            >
                                {t("common.confirm")}
                            </button>
                            <button
                                onClick={() => {
                                    setShowConfirm(null);
                                    setRejectionReason("");
                                }}
                                className="flex-1 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
                            >
                                {t("common.cancel")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Negotiation Tab Component
const NegotiationTab = ({ negotiationId }) => {
    const { t } = useTranslation();
    
    return (
        <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <div className="flex flex-col items-center justify-center gap-6">
                {/* Icon */}
                <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center">
                    <svg className="w-12 h-12 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                </div>
                
                {/* Text */}
                <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">
                        {t("order.negotiation.title")}
                    </h3>
                    <p className="text-gray-500 mb-6">
                        {t("order.negotiation.description")}
                    </p>
                </div>
                
                {/* Button */}
                <Link
                    to={`/home/negotiation/${negotiationId}`}
                    className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-dark transition-all duration-200 hover:scale-105 shadow-md hover:shadow-lg"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    {t("order.negotiation.goToNegotiation")}
                </Link>
            </div>
        </div>
    );
};

const OrderDetails = ({ currentUser }) => {
    const { t } = useTranslation();
    const { orderId } = useParams();
    const queryClient = useQueryClient();
    const [selectedStatus, setSelectedStatus] = useState("");
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showStatusForm, setShowStatusForm] = useState(false);
    const [cancelReason, setCancelReason] = useState("");
    const [images, setImages] = useState([]);
    const [mediaPreviews, setMediaPreviews] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [activeTab, setActiveTab] = useState('order');
    const [invoiceId, setInvoiceId] = useState(null);
    const [negotiationId, setNegotiationId] = useState(null);
    const fileInputRef = useRef(null);
    const [feedback, setFeedback] = useState("");

    const { data: order, isLoading, error } = useQuery({
        queryKey: ["order", orderId],
        queryFn: async () => {
            const response = await Client.get(`/orders/${orderId}`);
            
            // Extract invoice ID from order
            if (response.data.invoice?.id) {
                setInvoiceId(response.data.invoice.id);
            } else if (response.data.invoiceId) {
                setInvoiceId(response.data.invoiceId);
            }
            
            // Extract negotiation ID from order
            if (response.data.negotiation?.id) {
                setNegotiationId(response.data.negotiation.id);
            } else if (response.data.negotiationId) {
                setNegotiationId(response.data.negotiationId);
            }
            
            return response.data;
        },
        enabled: !!orderId,
    });

    if (isLoading) {
        return <div className="text-center mt-10 text-gray-600">{t("common.loading")}</div>;
    }

    if (error) {
        return <div className="text-center mt-10 text-red-500">{t("order.error")}</div>;
    }

    if (!order) {
        return <div className="text-center mt-10 text-red-500">{t("order.notFound")}</div>;
    }

    const isSeller = localStorage.getItem("user_id") === order?.negotiation?.sellerId;
    const isBuyer = localStorage.getItem("user_id") === order?.negotiation?.buyerId;
    const isServiceOrder = order?.negotiation?.type === "Service";

    const goodsStatusFlow = {
        Pending: isSeller ? ["Processing", "Cancelled"] : ["Cancelled"],
        Processing: isSeller ? ["Shipped", "Cancelled"] : ["Cancelled"],
        Shipped: isBuyer ? ["Delivered", "Cancelled"] : ["Cancelled"],
        Delivered: isBuyer ? ["Paid"] : [],
        Paid: [],
    };

    const serviceStatusFlow = {
        Pending: isSeller ? ["WaitingForGoods", "Cancelled"] : ["Cancelled"],
        WaitingForGoods: isBuyer ? ["DeliveredByBuyer", "Cancelled"] : ["Cancelled"],
        DeliveredByBuyer: isSeller ? ["ReceivedByProvider", "Cancelled"] : [],
        ReceivedByProvider: isSeller ? ["ReadyForPickup", "Cancelled"] : [],
        ReadyForPickup: isSeller ? ["DeliveredToClient", "Cancelled"] : [],
        DeliveredToClient: isBuyer ? ["ReceivedAndAccepted", "Cancelled"] : [],
        ReceivedAndAccepted: isBuyer ? ["Paid", "Cancelled"] : [],
        Paid: [],
    };

    const statusFlow = isServiceOrder ? serviceStatusFlow : goodsStatusFlow;
    const allowedStatuses = order?.status ? statusFlow[order.status] || [] : [];

    const translateStatus = (status) => {
        const statusMap = {
            "Pending": t("order.status.pending"),
            "Processing": t("order.status.processing"),
            "Shipped": t("order.status.shipped"),
            "Delivered": t("order.status.delivered"),
            "Paid": t("order.status.paid"),
            "Cancelled": t("order.status.cancelled"),
            "WaitingForGoods": t("order.status.waitingForGoods"),
            "DeliveredByBuyer": t("order.status.deliveredByBuyer"),
            "ReceivedByProvider": t("order.status.receivedByProvider"),
            "ReadyForPickup": t("order.status.readyForPickup"),
            "DeliveredToClient": t("order.status.deliveredToClient"),
            "ReceivedAndAccepted": t("order.status.receivedAndAccepted"),
        };
        return statusMap[status] || status;
    };

    const handleImageUpload = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                resolve({
                    fileObj: file,
                    preview: e.target.result
                });
            };
            reader.onerror = () => {
                reject(new Error(`Failed to process ${file.name}`));
            };
            reader.readAsDataURL(file);
        });
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e) => {
        if (!e.target.files?.length) return;

        try {
            const files = Array.from(e.target.files);
            const validFiles = files.filter(file => {
                const maxSizeMB = 10;
                if (file.size > maxSizeMB * 1024 * 1024) {
                    toast.error(t("order.image.tooBig", { maxSize: maxSizeMB }));
                    return false;
                }
                if (!file.type.startsWith('image/')) {
                    toast.error(t("order.image.invalidType"));
                    return false;
                }
                return true;
            });

            if (validFiles.length === 0) return;

            if (images.length + validFiles.length > 5) {
                toast.error(t("order.image.maxFiles"));
                return;
            }

            const results = await Promise.all(validFiles.map(file => handleImageUpload(file)));
            setMediaPreviews(prev => [...prev, ...results.map(r => r.preview)]);
            setImages(prev => [...prev, ...results.map(r => r.fileObj)]);
            e.target.value = '';
        } catch (error) {
            toast.error(t("order.image.uploadError"));
            console.error(error);
        }
    };

    const removeImage = (index) => {
        setImages(prev => prev.filter((_, i) => i !== index));
        setMediaPreviews(prev => prev.filter((_, i) => i !== index));
    };

    const uploadImages = async (files, category) => {
        try {
            const formData = new FormData();
            files.forEach((file) => {
                formData.append("images", file);
            });

            const response = await fetch(`${api_url}/files/upload-images/order/${order.id}?category=${category}`, {
                method: "POST",
                body: formData,
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("token"),
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || t("order.image.uploadFailed"));
            }

            return await response.json();
        } catch (error) {
            throw error;
        }
    };

    const updateOrderStatus = async (newStatus) => {
        setIsSubmitting(true);
        try {
            if (images.length > 0) {
                setIsUploading(true);
                try {
                    const category = newStatus === "Paid" ? "payment" : "delivery";
                    await uploadImages(images, category);
                    setImages([]);
                    setMediaPreviews([]);
                    toast.success(t("order.image.uploadSuccess"));
                } finally {
                    setIsUploading(false);
                }
            }

            if ((newStatus === "Delivered" || newStatus === "ReceivedAndAccepted") && !feedback.trim()) {
                toast.error(t("order.feedbackRequired"));
                return;
            }

            const response = await Client.patch(`/orders/${order.id}/status`, {
                status: newStatus,
                reason: cancelReason,
                feedback,
            });

            queryClient.invalidateQueries(["order", orderId]);
            setShowCancelModal(false);
            setCancelReason("");
            setFeedback("");
            toast.success(t("order.statusUpdateSuccess"));
            return response;

        } catch (error) {
            toast.error(error.message || t("order.statusUpdateFailed"));
            throw error;
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleStatusChange = (e) => {
        setSelectedStatus(e.target.value);
    };

    const handleStatusUpdate = async () => {
        if (selectedStatus === "Cancelled") {
            setShowCancelModal(true);
        } else {
            await updateOrderStatus(selectedStatus);
        }
    };

    const handleConfirmCancel = async () => {
        if (cancelReason.trim()) {
            await updateOrderStatus("Cancelled");
        }
    };

    return (
        <div dir="rtl" className="max-w-4xl mx-auto px-6 py-10">
            <h1 className="text-2xl font-bold text-primary mb-6">{t("order.title")}</h1>

            {/* Modern Tabs - Three tabs */}
            <div className="mb-8">
                <div className="flex gap-3 p-1.5 bg-gray-100 rounded-2xl w-full">
                    <button
                        onClick={() => setActiveTab('order')}
                        className={`
                            flex-1 flex items-center justify-center gap-3 py-3.5 px-5 rounded-xl font-semibold text-base transition-all duration-200
                            ${activeTab === 'order'
                                ? 'bg-primary text-white shadow-lg shadow-primary/20 scale-[1.02]'
                                : 'text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                            }
                        `}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                        </svg>
                        <span>{t("order.tabs.orderDetails")}</span>
                    </button>
                    
                    <button
                        onClick={() => setActiveTab('invoice')}
                        className={`
                            flex-1 flex items-center justify-center gap-3 py-3.5 px-5 rounded-xl font-semibold text-base transition-all duration-200
                            ${activeTab === 'invoice'
                                ? 'bg-primary text-white shadow-lg shadow-primary/20 scale-[1.02]'
                                : 'text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                            }
                        `}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span>{t("order.tabs.invoiceDetails")}</span>
                    </button>

                    <button
                        onClick={() => setActiveTab('negotiation')}
                        className={`
                            flex-1 flex items-center justify-center gap-3 py-3.5 px-5 rounded-xl font-semibold text-base transition-all duration-200
                            ${activeTab === 'negotiation'
                                ? 'bg-primary text-white shadow-lg shadow-primary/20 scale-[1.02]'
                                : 'text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                            }
                        `}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                        </svg>
                        <span>{t("order.tabs.negotiation")}</span>
                    </button>
                </div>
            </div>

            {/* Order Tab Content */}
            {activeTab === 'order' && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-xl shadow-md p-6"
                >
                    <OrderInfo
                        order={order}
                        setShowStatusForm={setShowStatusForm}
                        allowedStatuses={allowedStatuses}
                    />

                    {allowedStatuses.length > 0 && (
                        <div className="mt-4 flex gap-2 items-center">
                            <button
                                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                onClick={() => setShowStatusForm(true)}
                            >
                                {t("order.updateStatus")}
                            </button>
                        </div>
                    )}

                    {/* Status Update Section */}
                    {showStatusForm &&
                        <div className="mt-8 p-6 border rounded-lg">
                            <h2 className="text-xl font-bold mb-4">{t("order.updateStatusTitle")}</h2>

                            <div className="mb-4">
                                <select
                                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    value={selectedStatus}
                                    onChange={handleStatusChange}
                                >
                                    <option value="">{t("order.selectNewStatus")}</option>
                                    {allowedStatuses.map((status, index) => (
                                        <option key={index} value={status}>
                                            {translateStatus(status)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {(selectedStatus === "Delivered" || selectedStatus === "Paid" || selectedStatus === "ReceivedAndAccepted") && (
                                <div className="mb-4">
                                    <p className="font-bold mb-2">
                                        {selectedStatus === "Paid"
                                            ? t("order.uploadPaymentProof")
                                            : t("order.uploadDeliveryProof")}
                                    </p>
                                    <div
                                        className="w-full h-[20vh] border border-gray-300 bg-gray-50 rounded-md flex justify-center items-center text-gray-500 cursor-pointer"
                                        onClick={triggerFileInput}
                                    >
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            className="hidden"
                                            onChange={handleFileChange}
                                            accept="image/*"
                                            multiple
                                        />
                                        <span className="text-blue-500 underline">{t("order.uploadImages")}</span>
                                    </div>

                                    {mediaPreviews?.length > 0 && (
                                        <div className="mt-3">
                                            <p className="text-sm text-gray-600 mb-2">{t("order.uploadedImages")}</p>
                                            <div className="flex flex-wrap gap-3">
                                                {mediaPreviews.map((src, index) => (
                                                    <div key={index} className="relative">
                                                        <img
                                                            src={src}
                                                            className="w-16 h-16 object-cover border border-gray-300 rounded"
                                                            alt={`Preview ${index + 1}`}
                                                        />
                                                        <button
                                                            type="button"
                                                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                                                            onClick={() => removeImage(index)}
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <p className="text-xs text-gray-500 mt-1">
                                        {t("order.maxImages")}
                                    </p>

                                    {(selectedStatus === "Delivered" || selectedStatus === "ReceivedAndAccepted") && (
                                        <div className="mt-4">
                                            <label className="font-bold">{t("order.customerNotes")}</label>
                                            <textarea
                                                className="w-full p-2 border rounded mt-2"
                                                placeholder={t("order.customerNotesPlaceholder")}
                                                value={feedback}
                                                onChange={(e) => setFeedback(e.target.value)}
                                            ></textarea>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="flex justify-end gap-4">
                                <button
                                    type="button"
                                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                                    onClick={() => {
                                        setSelectedStatus("");
                                        setImages([]);
                                        setMediaPreviews([]);
                                        setShowStatusForm(false);
                                    }}
                                    disabled={isSubmitting || isUploading}
                                >
                                    {t("common.cancel")}
                                </button>
                                <button
                                    type="button"
                                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                                    disabled={!selectedStatus || isSubmitting || isUploading}
                                    onClick={handleStatusUpdate}
                                >
                                    {isUploading ? t("order.uploading") : isSubmitting ? t("order.saving") : t("common.confirm")}
                                </button>
                            </div>
                        </div>
                    }

                    {showCancelModal && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white p-6 rounded-xl shadow-xl w-96">
                                <h2 className="text-xl font-bold mb-4 text-red-600">{t("order.confirmCancel")}</h2>
                                <textarea
                                    className="w-full p-2 border rounded h-24 focus:outline-none focus:ring-2 focus:ring-red-400"
                                    placeholder={t("order.cancelReasonPlaceholder")}
                                    value={cancelReason}
                                    onChange={(e) => setCancelReason(e.target.value)}
                                ></textarea>
                                <div className="flex justify-end gap-4 mt-4">
                                    <button
                                        className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                                        onClick={() => setShowCancelModal(false)}
                                        disabled={isSubmitting}
                                    >
                                        {t("common.cancel")}
                                    </button>
                                    <button
                                        className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                                        onClick={handleConfirmCancel}
                                        disabled={!cancelReason.trim() || isSubmitting}
                                    >
                                        {isSubmitting ? t("order.cancelling") : t("common.confirm")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </motion.div>
            )}

            {/* Invoice Tab Content */}
            {activeTab === 'invoice' && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <InvoiceDetailsTab invoiceId={invoiceId} />
                </motion.div>
            )}

            {/* Negotiation Tab Content */}
            {activeTab === 'negotiation' && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <NegotiationTab negotiationId={negotiationId} />
                </motion.div>
            )}
        </div>
    );
};

export default OrderDetails;

export const translateStatus = (status) => {
    switch (status) {
        case "Pending": return "قيد الانتظار";
        case "Processing": return "قيد المعالجة";
        case "Shipped": return "تم الشحن";
        case "Delivered": return "تم التوصيل";
        case "Paid": return "تم الدفع";
        case "Cancelled": return "إلغاء";
        case "WaitingForGoods" : return "في انتظار استلام البضاعة";
        case "DeliveredByBuyer": return "تم التسليم لمقدم الخدمة ";
        case "ReceivedByProvider": return "تم استلام البضاعة ";
        case "ReadyForPickup": return "جاهز للاستلام";
        case "DeliveredToClient": return "تم التسليم للعميل";
        case "ReceivedAndAccepted": return "تم الاستلام والقبول";
        default: return status;
    }
};